import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import images from "../../assets/images";
import Footer from "../../components/footer";
import Header from "../../components/header";
import CustomModal from "../../components/modal";
import { useTranslate } from "../../context/TranslationContext";
import {
  useGetCampaignInternalListQuery,
  useUpdateCampaignStatusMutation,
} from "../../redux/Apis/Campaign";
import { useUpdateCourseMutation } from "../../redux/Apis/Course";
import {
  useGetCourseExpiryNotificationsQuery,
  useGetCourseNotificationsQuery,
} from "../../redux/Apis/Notification";

const Notifications = () => {
  const { t } = useTranslate("notifications");
  const lang = useSelector((state) => state.language.lang);
  const navigate = useNavigate();
  const { isLoggedIn } = useSelector((state) => state.authSlice);
  const [modalConfig, setModalConfig] = useState({
    show: false,
    type: null, // "extend" | "archive" | "success"
    item: null,
  });
  const [selectedMonths, setSelectedMonths] = useState("6");
  const [loading, setLoading] = useState(false);

  const {
    data: campaignData,
    isLoading,
    refetch,
  } = useGetCampaignInternalListQuery({ lang }, { skip: !isLoggedIn });
  const {
    data: courseData,
    isLoading: courseLoading,
    refetch: refetchCourse,
  } = useGetCourseNotificationsQuery(
    { params: { lang } },
    { skip: !isLoggedIn }
  );
  const {
    data: expiryData,
    isLoading: expiryLoading,
    refetch: refetchExpiry,
  } = useGetCourseExpiryNotificationsQuery(
    { params: { lang } },
    { skip: !isLoggedIn }
  );

  const [updateStatus] = useUpdateCampaignStatusMutation();
  const [updateCourse] = useUpdateCourseMutation();
  const [expandedId, setExpandedId] = useState(null);
  const [dismissedIds, setDismissedIds] = useState([]);

  const openExtendModal = (item) => {
    setSelectedMonths("6");
    setModalConfig({ show: true, type: "extend", item });
  };

  const openArchiveModal = (item) => {
    setModalConfig({ show: true, type: "archive", item });
  };

  const closeModal = () => {
    setModalConfig({ show: false, type: null, item: null });
  };

  useEffect(() => {
    if (isLoggedIn) {
      refetch();
      refetchExpiry();
      refetchCourse();
    }
  }, [refetch, isLoggedIn, refetchExpiry, refetchCourse]);

  const campaignNotifications = (campaignData?.data || [])
    .filter((item) => !item.isactive && !dismissedIds.includes(item._id))
    .map((item) => ({
      ...item,
      isCampaign: true,
      title: item.campaignid?.title || "New Message",
      message: item.campaignid?.messagebody || "",
      cta: item.campaignid?.ctalink,
      ctaLabel: item.campaignid?.ctabutton,
      expire: item.campaignid?.expirationdate,
      viewed: item.view,
    }));

  const expiryNotifications = (expiryData?.data || []).map((item) => ({
    ...item,
    isCampaign: false,
    isExpiry: true,
    title: item.title,
    message: item.message,
    expiry_date: item.expiry_date,
  }));

  const courseNotifications = (courseData?.data || []).map((item) => ({
    ...item,
    isCampaign: false,
    title: item.title,
    message: item.message,
    viewed: item.read,
  }));

  const allNotifications = [
    ...expiryNotifications.map((item) => ({
      ...item,
      _uniqueId: `expiry-${item._id}`,
    })),
    ...courseNotifications.map((item) => ({
      ...item,
      _uniqueId: `course-${item._id}`,
    })),
    ...campaignNotifications.map((item) => ({
      ...item,
      _uniqueId: `campaign-${item._id}`,
    })),
  ];

  const handleTitleClick = async (item) => {
    setExpandedId((prev) => (prev === item._uniqueId ? null : item._uniqueId));

    if (item.isCampaign && !item.viewed) {
      try {
        const payload = {
          _id: item._id,
          view: true,
          isactive: false,
          campaignid: item.campaignid?._id,
        };
        await updateStatus({ payload, params: { lang } });
        refetch();
      } catch (err) {
        console.error("Campaign view update error:", err);
      }
    }

    if (!item.isCampaign && !item.isExpiry && !item.viewed) {
      const payload = {
        _id: item._id,
        read: true,
      };
      await updateCourse({ payload, params: { lang } });
      refetch();
    }
  };

  const handleDismiss = async (id) => {
    setDismissedIds((prev) => [...prev, id]);
    await updateStatus({ _id: id, isactive: true });
  };

  const handleModalAction = async () => {
    setLoading(true);
    try {
      if (modalConfig.type === "extend") {
        const months = parseInt(selectedMonths, 10);
        const newDate = new Date(modalConfig.item.expiry_date);
        newDate.setMonth(newDate.getMonth() + months);
        const payload = {
          _id: modalConfig.item._id,
          expiry_date: newDate.toISOString().split("T")[0],
          expiry_action: true,
        };
        await updateCourse({
          payload,
          params: { lang },
        });
      }

      if (modalConfig.type === "archive") {
        const payload = {
          _id: modalConfig.item._id,
          status: "archived",
          expiry_action: true,
        };
        await updateCourse({
          payload,
          params: { lang },
        });
      }

      setModalConfig({
        show: true,
        type: "success",
        item: null,
      });
      refetchExpiry();
      refetchCourse();
    } catch (err) {
      console.error("Modal action error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRepublish = (item) => {
    navigate(`/course/details/${item._id}`, {
      state: {
        republish: true,
        courseId: item._id,
        activeTab: "v-pills-pricing",
      },
    });
  };

  return (
    <>
      <Header />
      <div className="banner back-transparent">
        <div className="container py-4">
          <h2 className="mb-4">{t("title")}</h2>

          {isLoading || courseLoading || !courseData || !campaignData ? (
            <Spinner />
          ) : allNotifications.length === 0 ? (
            <div className="text-center">
              <img
                src={images.modal.no_notification}
                alt={t("noNotifications")}
                className="mb-3 w-50 h-50"
              />
              {/* <p>{t("noNotifications")}</p> */}
            </div>
          ) : (
            allNotifications.map((item) => (
              <div
                key={item._id}
                className="rounded p-3 mb-3 shadow-sm"
                style={{ backgroundColor: "#0e0e18" }}
              >
                <div className="d-flex justify-content-between align-items-start text-start">
                  <div className="flex-grow-1">
                    <h5
                      className="mb-2 purple-text text-start d-flex align-items-center justify-content-between"
                      onClick={() => handleTitleClick(item)}
                      style={{ cursor: "pointer" }}
                    >
                      {item.isCampaign ? item.title : `Course: ${item.title}`}

                      {/* 👉 Notification Type Icons */}
                      {item.isExpiry && (
                        <i
                          className="fa-solid fa-hourglass-half ms-2 text-warning"
                          title={t("expiryNotification")}
                        ></i>
                      )}
                      {!item.isCampaign && !item.isExpiry && (
                        <i
                          className="fa-solid fa-bell ms-2 text-info"
                          title={t("courseNotification")}
                        ></i>
                      )}
                      {item.isCampaign && (
                        <i
                          className="fa-solid fa-bullhorn ms-2 text-danger"
                          title={t("campaignNotification")}
                        ></i>
                      )}
                    </h5>

                    {expandedId === item._uniqueId && (
                      <>
                        <p className="mb-2">{item.message}</p>

                        {item.cta && item.ctaLabel && (
                          <a
                            href={item.cta}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <button className="site-btn border-btn">
                              {item.ctaLabel}
                            </button>
                          </a>
                        )}

                        {item.expire && (
                          <p className="badge ms-3 text-end float-end flex-grow-1 mb-0 mt-2">
                            {t("expiresOn")} {item.expire}
                          </p>
                        )}
                      </>
                    )}
                    {expandedId == item._uniqueId && item.isExpiry && (
                      <div className="mt-2">
                        <button
                          className="site-btn me-2 mt-2"
                          onClick={() => openExtendModal(item)}
                        >
                          {t("extendButton")}
                        </button>
                        <button
                          className="site-btn border-btn me-2 mt-2"
                          onClick={() => openArchiveModal(item)}
                        >
                          {t("archiveButton")}
                        </button>
                        <button
                          className="site-btn border-btn mt-2"
                          onClick={() => handleRepublish(item)}
                        >
                          {t("editRepublishButton")}
                        </button>
                      </div>
                    )}
                  </div>

                  {item.isCampaign && (
                    <button
                      onClick={() => handleDismiss(item._id)}
                      className="btn btn-sm btn-light ms-3"
                      title={t("dismiss")}
                    >
                      &times;
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <CustomModal
        show={modalConfig.show}
        close={closeModal}
        success={modalConfig.type === "success"}
        heading={
          modalConfig.type === "extend"
            ? t("extendModalTitle")
            : modalConfig.type === "archive"
            ? t("archiveModalTitle")
            : t("successModalTitle")
        }
        action={handleModalAction}
        loading={loading}
      >
        {modalConfig.type === "extend" && (
          <div className="mt-3">
            <select
              className="site-input w-170 mb-2 border border-1"
              value={selectedMonths}
              onChange={(e) => setSelectedMonths(e.target.value)}
            >
              <option value="6">{t("extend6")}</option>
              <option value="12">{t("extend12")}</option>
            </select>
          </div>
        )}
      </CustomModal>

      <Footer />
    </>
  );
};

export default Notifications;
